import {
  ClientMsg,
  ServerMsg,
  Snapshot,
  decodeSnapshot,
  PROTOCOL_VERSION,
} from '@voidline/shared';
import { loadResumeToken, saveResumeToken } from '../persist.js';

export type ConnState = 'idle' | 'connecting' | 'connected' | 'reconnecting' | 'failed';

/**
 * WebSocket wrapper: JSON control messages + binary snapshots, ping measurement,
 * and automatic resume-token reconnection after network interruptions.
 */
export class Connection {
  private ws: WebSocket | null = null;
  state: ConnState = 'idle';
  playerId = '';
  resumeToken = '';
  rtt = 0;
  private pingTimer: ReturnType<typeof setInterval> | null = null;
  private pingSentAt = 0;
  private reconnectAttempts = 0;
  private intentionalClose = false;
  private url: string;

  onMessage: ((msg: ServerMsg) => void) | null = null;
  onSnapshot: ((snap: Snapshot) => void) | null = null;
  onStateChange: ((state: ConnState) => void) | null = null;

  constructor(
    serverUrl: string,
    private playerName: string
  ) {
    this.url = serverUrl || defaultUrl();
  }

  connect(): void {
    this.intentionalClose = false;
    this.open(loadResumeToken() ?? undefined);
  }

  private open(resumeToken?: string): void {
    this.setState(this.reconnectAttempts > 0 ? 'reconnecting' : 'connecting');
    try {
      this.ws = new WebSocket(this.url);
    } catch (err) {
      console.error('[net] websocket ctor failed', err);
      this.setState('failed');
      return;
    }
    this.ws.binaryType = 'arraybuffer';

    const connectTimeout = setTimeout(() => {
      if (this.state === 'connecting' || this.state === 'reconnecting') {
        this.ws?.close();
      }
    }, 6000);

    this.ws.onopen = () => {
      clearTimeout(connectTimeout);
      this.send({
        type: 'hello',
        name: this.playerName || 'PILOT',
        protocolVersion: PROTOCOL_VERSION,
        resumeToken,
      });
    };

    this.ws.onmessage = (ev) => {
      if (typeof ev.data === 'string') {
        let msg: ServerMsg;
        try {
          msg = JSON.parse(ev.data);
        } catch {
          return;
        }
        if (msg.type === 'welcome') {
          this.playerId = msg.playerId;
          this.resumeToken = msg.resumeToken;
          saveResumeToken(msg.resumeToken);
          this.reconnectAttempts = 0;
          this.setState('connected');
          this.startPing();
        } else if (msg.type === 'pong') {
          this.rtt = Math.round(performance.now() - this.pingSentAt);
        }
        this.onMessage?.(msg);
      } else if (ev.data instanceof ArrayBuffer) {
        const snap = decodeSnapshot(new DataView(ev.data));
        if (snap) this.onSnapshot?.(snap);
      }
    };

    this.ws.onclose = () => {
      clearTimeout(connectTimeout);
      this.stopPing();
      if (this.intentionalClose) {
        this.setState('idle');
        return;
      }
      // auto-reconnect with the resume token (network blip / server restart)
      if (this.reconnectAttempts < 4) {
        this.reconnectAttempts++;
        const delay = Math.min(4000, 350 * Math.pow(2, this.reconnectAttempts));
        this.setState('reconnecting');
        setTimeout(() => {
          if (!this.intentionalClose) this.open(this.resumeToken || undefined);
        }, delay);
      } else {
        this.setState('failed');
      }
    };

    this.ws.onerror = () => {
      // onclose will follow
    };
  }

  private setState(s: ConnState): void {
    if (this.state !== s) {
      this.state = s;
      this.onStateChange?.(s);
    }
  }

  private startPing(): void {
    this.stopPing();
    this.pingTimer = setInterval(() => {
      this.pingSentAt = performance.now();
      this.send({ type: 'ping', t: this.pingSentAt, rtt: this.rtt });
    }, 2000);
  }

  private stopPing(): void {
    if (this.pingTimer) clearInterval(this.pingTimer);
    this.pingTimer = null;
  }

  send(msg: ClientMsg): void {
    if (this.ws?.readyState === WebSocket.OPEN) this.ws.send(JSON.stringify(msg));
  }

  sendBinary(buf: ArrayBuffer): void {
    if (this.ws?.readyState === WebSocket.OPEN) this.ws.send(buf);
  }

  close(): void {
    this.intentionalClose = true;
    this.stopPing();
    this.ws?.close();
    this.ws = null;
    this.setState('idle');
  }

  get connected(): boolean {
    return this.state === 'connected';
  }
}

function defaultUrl(): string {
  const proto = location.protocol === 'https:' ? 'wss' : 'ws';
  return `${proto}://${location.host}/ws`;
}
